import React, { useEffect } from 'react';
import type { Question, ResponsePayload } from '../../../types';
import { registerQuestionRenderer, type QuestionRendererComponent } from './registry';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';

const backendOnlyTypes = [
  'expandable_list',
  'short_write_in',
  'title',
  'image_as_button',
  'fill_in_the_blank',
];

const UnsupportedQuestionDisplay: React.FC<{ question: Question }> = ({ question }) => (
  <div className="flex w-full flex-col gap-2 bg-transparent p-3">
    <Alert variant="info" className="mb-2">
      <AlertTitle>Unsupported Question Type</AlertTitle>
      <AlertDescription>
        This question type ({question.question_type_display}) is not yet supported in the frontend but has been uploaded successfully to the backend.
      </AlertDescription>
    </Alert>

    <div className="max-h-[60vh] overflow-auto rounded-2xl border border-white/10 bg-white/5 p-3">
      <h3 className="mb-2 text-base font-semibold text-primary">
        Complete Question Data:
      </h3>

      <div className="flex flex-col gap-1.5">
        <div>
          <p className="font-semibold text-muted-foreground">
            Question ID:
          </p>
          <p className="text-sm">{question.id || 'Not assigned'}</p>
        </div>

        <div>
          <p className="font-semibold text-muted-foreground">
            Question Type:
          </p>
          <p className="text-sm">
            {question.question_type_display} ({question.question_type})
          </p>
        </div>

        <div>
          <p className="font-semibold text-muted-foreground">
            Question Text:
          </p>
          <p className="whitespace-pre-wrap text-sm">
            {question.text || 'No text provided'}
          </p>
        </div>

        <div>
          <p className="font-semibold text-muted-foreground">
            Options:
          </p>
          <pre className="whitespace-pre-wrap text-sm">
            {JSON.stringify(question.options ?? [], null, 2)}
          </pre>
        </div>

        <div>
          <p className="font-semibold text-muted-foreground">
            Metadata:
          </p>
          <pre className="whitespace-pre-wrap text-sm">
            {JSON.stringify(question.metadata ?? {}, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  </div>
);

const UnsupportedRenderer: QuestionRendererComponent = ({
  question,
  existingResponse,
  onDraftChange,
  onHasUserInputChange,
  onValidityChange,
  onExtrasChange,
}) => {
  void onExtrasChange;
  const questionId = question?.id ?? existingResponse?.question ?? -1;

  useEffect(() => {
    onHasUserInputChange?.(true);
    onValidityChange?.(true);

    if (questionId < 0) {
      onDraftChange(null);
      return;
    }

    const payload: ResponsePayload = {
      question: questionId,
    };

    onDraftChange(existingResponse ?? payload);
  }, [
    existingResponse,
    onDraftChange,
    onHasUserInputChange,
    onValidityChange,
    questionId,
  ]);

  return <UnsupportedQuestionDisplay question={question} />;
};

registerQuestionRenderer(backendOnlyTypes, UnsupportedRenderer);
